import BaseComponent from "../../../baseComponent.js";
import InternalEvent from "../../../../core/constants/internalEvent.js";
import WidgetService from "../../../../core/services/widgetService.js";
import DashboardService from "../../../../core/services/dashboardService";
import {TradingViewApi} from "./api";
import {getDefaultChartState, getNewSource, getNewPane, getMainPane} from "./chartState";
import UniqueIdGenerator from "../../../../core/utils/uniqueIdGenerator";
import TradingViewLegends from "./legends.jsx";
import styles from "./styles.scss";
import {widget} from "../../../../vendors/tradingview/charting_library.min";

const FIBONACCI = "FIBONACCI";
const indicatorList = ["SMA", "MACD", "RSI", "FIBONACCI", "BOLLINGER BANDS"];

export default class TradingViewWidget extends BaseComponent {
  constructor(props) {
    super(props);
    const widget = this.getWidget();
    // this.disableIndicators(widget.legends);
    this.chartId = UniqueIdGenerator.id();
    this.state = {
      widget: this.getWidget()
    };
    this.interval = null;
    this.chartEntities = {};
    this.currentInputs = {};
  }

  getWidget() {
    return WidgetService.getWidget(this.props.widget.id)
  }

  componentDidMount() {
    this.tradingViewApi = new TradingViewApi({
      widget: this.getWidget(),
      getResolutionInMillis: this.getResolutionInMillis
    });
    this.buildChartState();
    this.renderChart();
  }

  handleChartLegendClicked(legendId) {
    const widgetCopy = this.deepCopy(this.state.widget);
    const legendIndex = widgetCopy.legends.findIndex(l => l.id === legendId);
    const legend = widgetCopy.legends[legendIndex];
    legend.parameters.isActive = !legend.parameters.isActive;
    this.setState({widget: widgetCopy});
    if (legend.parameters.isIndicator) {
      this.toggleIndicator(legend);
    } else {
      const chart = this.getActiveChart();
      chart.setEntityVisibility(legendId, legend.parameters.isActive)
    }
    WidgetService.updateWidgetLegends(widgetCopy.id, widgetCopy.legends);
    this.publish(InternalEvent.Dashboard.UPDATE_DASHBOARD_STATE);
  }

  handleIndicatorInputChange(legend, inputs) {
    const widgetCopy = this.deepCopy(this.state.widget);
    const targetLegend = widgetCopy.legends.find(l => l.id === legend.id);
    targetLegend.parameters.indicatorInputs = inputs;
    this.setState({widget: widgetCopy});
    this.toggleIndicator(legend, inputs);


    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }

    this.updateTimer = setTimeout(() => {
      WidgetService.updateWidgetLegends(widgetCopy.id, widgetCopy.legends);
      this.publish(InternalEvent.Dashboard.UPDATE_DASHBOARD_STATE);
    }, 500);
  }

  defaultInputs(label) {
    switch (label) {
      case "SMA" :
        return [9, "close", 0];
      case "MACD" :
        return [12, 26, "close", 9];
      case "RSI" :
        return [14];
      case "BOLLINGER BANDS" :
        return [20, 2];
    }
  }

  toggleIndicator(legend, inputs) {
    const chart = this.getActiveChart();
    const legendParmas = legend.parameters;
    if (!inputs) {
      inputs = legendParmas.indicatorInputs || this.defaultInputs(legendParmas.legendLabel)
    }

    switch (legendParmas.legendLabel) {
      case "SMA": {
        // [length, source, offset]
        this.removeEntity("SMA");
        if (legendParmas.isActive) {
          this.chartEntities["SMA"] = chart.createStudy('Moving Average', false, false, inputs, null, {
            'Plot.linewidth': 1,
            'Plot.color': legendParmas.legendColor
          });
        }
        break;
      }
      case "MACD": {
        // [fastLength, slowLength, source, signalLength]
        this.removeEntity("MACD");
        if (legendParmas.isActive) {
          this.chartEntities["MACD"] = chart.createStudy('MACD', false, false, inputs, null, {
            'MACD.linewidth': 1,
            'MACD.color': legendParmas.legendColor,
            'Signal.plottype': "circles",
            'Signal.color': legendParmas.legendColor,
            'Histogram.linewidth': 4,
            'Histogram.color': "rgba(223, 221, 221, 0.65)"
          });
        }
        break;
      }
      case "RSI": {
        // [length]
        this.removeEntity("RSI");
        if (legendParmas.isActive) {
          this.chartEntities["RSI"] = chart.createStudy('Relative Strength Index', false, false, inputs, null, {
            'Plot.linewidth': 1,
            'Plot.color': legendParmas.legendColor,
            'UpperLimit.value': 80,
            'LowerLimit.value': 20,
          });
        }
        break;
      }
      case "BOLLINGER BANDS": {
        // [length, mult]
        this.removeEntity("BOLLINGER BANDS");
        if (legendParmas.isActive) {
          this.chartEntities["BOLLINGER BANDS"] = chart.createStudy('Bollinger Bands', false, false, inputs, null, {
            'Plot.linewidth': 1,
            'Plot.color': legendParmas.legendColor
          });
        }
        break;
      }
      case "FIBONACCI": {
        this.removeEntity("FIBONACCI");
        if (legendParmas.isActive) {
          const dataRange = this.tradingViewApi.getMainSeriseDataRange();
          const margin = Math.round((dataRange.to - dataRange.from) * 0.15);
          const from = Math.round(dataRange.from + margin);
          const to = Math.round(dataRange.to - margin);
          const fibRange = this.tradingViewApi.getFibRange({from, to});
          if (fibRange) {
            this.chartEntities["FIBONACCI"] = chart.createMultipointShape([{time: fibRange.from}, {time: fibRange.to}], {
              shape: 'fib_retracement'
            });
          }
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  hideEntities(legends) {
    legends.forEach(l => {
      const chart = this.getActiveChart();
      if (!l.parameters.isIndicator && !l.parameters.isActive) {
        chart.setEntityVisibility(l.id, l.parameters.isActive);
      }
    });
  }

  removeEntity(label) {
    const entityId = this.chartEntities[label];
    if (entityId) {
      const chart = this.getActiveChart();
      chart.removeEntity(entityId);
      delete this.chartEntities[label];
    }
  }

  renderChart() {
    if (this.chartWidget) {
      clearInterval(this.interval);
      this.chartWidget.remove();
    }

    this.chartWidget = window.tvWidget = new TradingView.widget({
      debug: false, // uncomment this line to see Library errors and warnings in the console
      autosize: true,
      symbol: "Loading",
      interval: "1",
      container_id: this.chartId,
      datafeed: this.tradingViewApi,
      library_path: "/vendors/tradingview/",
      locale: "en",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      drawings_access: {type: "black", tools: [{name: "Regression Trend"}]},
      enabled_features: ["logo_without_link"],
      disabled_features: this.getDisableFeatures(),
      charts_storage_api_version: "1.1",
      client_id: "tradingview.com",
      user_id: "public_user_id",
      custom_css_url: this.props.isAddWidget ? "" : "tradingview.css",

    });


    this.chartWidget.onChartReady(_ => {

      this.chartWidget.load(this.chartState);
      this.chartWidget.subscribe("onAutoSaveNeeded", _ => {
        this.updateChartState();
      });

      this.chartWidget.chart().dataReady(_ => {
        this.interval = setInterval(() => {
          const dataRange = this.tradingViewApi.getMainSeriseDataRange();
          const viewRange = this.getActiveChart().getVisibleRange();
          //
          // console.log(`Data  Range: ${dataRange.from}-${dataRange.to}`);
          // console.log(`Chart Range: ${viewRange.from}-${viewRange.to}`);
          // console.log(`==============================================`);

          if (dataRange.from == null || dataRange.to == null) {
            clearInterval(this.interval);
            return;
          }

          const fromDiff = dataRange.from - viewRange.from;
          const toDiff = dataRange.to - viewRange.to;

          if (isNaN(dataRange.from) || isNaN(dataRange.to)) {
            clearInterval(this.interval);
            return;
          }

          if (this.fromDiff === fromDiff && this.toDiff === toDiff) {
            clearInterval(this.interval);
            return;
          }

          this.fromDiff = fromDiff;
          this.toDiff = toDiff;
          this.getActiveChart().setVisibleRange(dataRange);
        }, 300);

        this.state.widget.legends.forEach(l => {
          this.toggleIndicator(l);
        });
        this.hideEntities(this.state.widget.legends);
      });
      // this.chartWidget.chart().crossHairMoved(e => console.log(e));
    });
  }

  getActiveChart() {
    return this.chartWidget.activeChart();
  }

  getDisableFeatures() {
    return this.props.isAddWidget ? ["create_volume_indicator_by_default"] : [
      "create_volume_indicator_by_default",
      "legend_context_menu",
      "left_toolbar",
      "header_widget_dom_node",
      "timeframes_toolbar",
      "edit_buttons_in_legend",
      "display_market_status"];
  }

  updateChartState() {
    this.chartWidget.save(state => {
      if (this.props.isAddWidget) {
        WidgetService.updateWidgetMetaData(this.props.widget.id, {
          tradingViewChart: state,
        });
      }
    });
  }

  componentWillUnmount() {
    if (this.chartWidget) {
      try {
        this.chartWidget.remove();
      } catch (e) {
      }

    }
    this.unsubscribeAll();
    clearInterval(this.interval);
    this.tradingViewApi.destory();
  }


  update(data) {
  }

// temporary solution for getting resolution from the dashboard variables.
  getTimeWindowUnitVriable() {
    const {timeWindowUnit, timeWindowValue} = this.getTimeWindowUnits();
    switch (timeWindowUnit) {
      case 1:
        return timeWindowValue + "";
      case 3:
        return timeWindowValue + "D";
      case 4:
        return timeWindowValue + "W";
      case 5:
        return timeWindowValue + "M";
      case 6:
        return 12 * timeWindowValue + "M";
      case 8:
        return 3 * timeWindowValue + "M";
    }
  }

  getResolutionInMillis() {
    const {timeWindowUnit, timeWindowValue} = this.getTimeWindowUnits();
    switch (timeWindowUnit) {
      case 1:
        return timeWindowValue * 1000 * 60; // minute
      case 3:
        return timeWindowValue * 1000 * 60 * 60 * 24; // day
      case 4:
        return timeWindowValue * 1000 * 60 * 60 * 24 * 7; // week
      case 5:
        return timeWindowValue * 1000 * 60 * 60 * 24 * 30;  // month
      case 6:
        return 12 * timeWindowValue * 1000 * 60 * 60 * 24 * 30; // month
      case 8:
        return 3 * timeWindowValue * 1000 * 60 * 60 * 24 * 30;  // month
    }
  }

  getTimeWindowUnits() {
    const variables = DashboardService.getCurrentDashboardVariables();
    let timeWindowUnit;
    let timeWindowValue = 1;

    Object.keys(variables).forEach(k => {
      if (variables[k].name === "TimeWindowUnit") {
        timeWindowUnit = parseInt(variables[k].value);
      }

      if (variables[k].name === "TimeWindowValue") {
        timeWindowValue = parseInt(variables[k].value);
      }
    });

    return {timeWindowUnit, timeWindowValue};
  }

  buildChartState() {
    const widget = this.getWidget();
    let chartState = getDefaultChartState();
    const resolution = this.getTimeWindowUnitVriable();

    if (widget.legends) {
      // Setting main-series. Main-series must be from the first legend.
      let mainPaneNumber = widget.legends[0].parameters.pane;
      addNewPane(mainPaneNumber, true, widget.legends[0].id);
      chartState.charts[0].panes[mainPaneNumber].stretchFactor = 10000;
      chartState.charts[0].panes[mainPaneNumber].sources[0].state.symbol = widget.legends[0].id;
      chartState.charts[0].panes[mainPaneNumber].sources[0].state.shortName = widget.legends[0].id;
      chartState.charts[0].panes[mainPaneNumber].sources[0].state.lineStyle.color = widget.legends[0].parameters.legendColor;
      // chartState.charts[0].panes[mainPaneNumber].sources[0].state.lineStyle.linewidth = 2;

      const sourceIndexes = [];
      sourceIndexes[mainPaneNumber] = 0;
      for (let i = 1; i < widget.legends.length; i++) {
        if (!widget.legends[i].parameters.isIndicator) {
          const paneNumber = widget.legends[i].parameters.pane;
          addNewPane(paneNumber);

          if (sourceIndexes[paneNumber] === undefined) {
            sourceIndexes[paneNumber] = 0;
          } else {
            sourceIndexes[paneNumber]++;
          }

          let source = chartState.charts[0].panes[paneNumber].sources.find(s => s.id === widget.legends[i].id);

          chartState.charts[0].panes[paneNumber].stretchFactor = 10000;

          if (!source) {
            const sourceIndex = sourceIndexes[paneNumber];
            source = getNewSource(widget.legends[i].id, widget.legends[i].id);
            source.state.inputs.symbol = widget.legends[i].id;
            source.state.lineStyle.color = widget.legends[i].parameters.legendColor;
            // source.state.lineStyle.linewidth = 2;
            chartState.charts[0].panes[paneNumber].sources[sourceIndex] = source;
          } else {
            source.state.inputs.symbol = widget.legends[i].id;
            source.state.lineStyle.color = widget.legends[i].parameters.legendColor;
            // source.state.lineStyle.linewidth = 2;
          }
        }
      }

      if (resolution) {
        chartState.charts[0].panes.forEach(p => {
          p.sources.forEach(s => {
            s.state.interval = resolution;
          });
        });
      }
    }

    const showDetail = this.props.isAddWidget || false;
    chartState.charts[0].chartProperties.paneProperties.legendProperties = {
      "showStudyArguments": showDetail,
      "showStudyTitles": showDetail,
      "showStudyValues": showDetail,
      "showSeriesTitle": showDetail,
      "showSeriesOHLC": showDetail,
      "showLegend": showDetail,
    };

    this.chartState = chartState;
    this.chartStateOriginal = this.deepCopy(chartState);

    // this.disableSources();

    function addNewPane(paneNumber, isMain, id) {
      if (chartState.charts[0].panes[paneNumber] === undefined) {
        chartState.charts[0].panes[paneNumber] = isMain ? getMainPane(id) : getNewPane();
      }
    }
  }

// disable all indicators by default
  disableIndicators(legends) {
    legends.forEach(l => {
      const label = l.parameters.legendLabel;
      if (indicatorList.indexOf(label) > -1) {
        l.parameters.isActive = false;
      }
    });
  }

  render() {
    return (
      <div className={styles.container}>
        <div className={styles.chartContainer} onClick={e => console.log('click')}>
          <div id={this.chartId} style={{height: "100%"}}/>
        </div>
        {/*<div className={styles.legendsWrapper}>*/}
        {/*<Legends widgetId={this.state.widget.id}*/}
        {/*legends={this.state.widget.legends}*/}
        {/*metaData={this.state.widget.metaData}/>*/}
        {/*</div>*/}
        {/*<div className={styles.screen} ref="screen" ></div>*/}
        <TradingViewLegends legends={this.state.widget.legends}
                            legendClick={this.handleChartLegendClicked}
                            defaultInputs={this.defaultInputs}
                            indicatorInputChange={this.handleIndicatorInputChange}/>
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/components/dashboard/widgets/trading-view/tradingViewWidget.jsx